import React, { useState, useEffect } from 'react';
import { ActionIcon, Group, Loader, Paper, Stack, Text, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { IconCurrencyDollar, IconReceipt, IconTrash } from '@tabler/icons-react';
import apiClient from './services/apiClient.js';
import { setSelectedVehicle } from './redux/vehicleSlice.js';

const PAGE_SIZE = 10;

const formatPlate = (plate) => {
    const clean = plate.replace(/[^A-Z0-9]/gi, '').toUpperCase();
    return clean.length === 6 ? `${clean.slice(0, 3)}-${clean.slice(3)}` : clean;
};

const formatCurrency = (val) =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(val);

const formatDateTime = (iso) =>
    new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });

const DriveOffReportPage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
    const [allRows, setAllRows] = useState([]);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!isAuthenticated) return;
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const accessToken = await getAccessTokenSilently({
                    authorizationParams: { audience: process.env.REACT_APP_AUTH0_API_AUDIENCE },
                });
                const groupName = user?.['/roles']?.[0] || 'servo1';
                const result = await apiClient.getVehicleDriveOffsAsync(
                    { groupName, pageNumber: 1, batchSize: 200 },
                    accessToken
                );
                if (!cancelled) setAllRows(Array.isArray(result) ? result : []);
            } catch (err) {
                if (!cancelled) {
                    setError('Failed to load drive-offs.');
                    console.error(err);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [isAuthenticated]);

    const handlePlateClick = async (plate) => {
        const normalizedPlate = plate.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        if (!isAuthenticated || !user?.['/roles']?.length) {
            navigate(`/vehicle/${normalizedPlate}`);
            return;
        }
        try {
            const accessToken = await getAccessTokenSilently({
                authorizationParams: { audience: process.env.REACT_APP_AUTH0_API_AUDIENCE },
            });
            const result = await apiClient.getVehicleHistoryAsync({
                plate: normalizedPlate,
                groupName: user['/roles'][0],
                pageNo: 1,
                pageBatchSize: Number(process.env.REACT_APP_VEHICLE_HISTORY_BATCH_SIZE || 20) + 1,
            }, accessToken);
            if (result?.length) {
                const vehicle = result[0];
                dispatch(setSelectedVehicle(vehicle));
                const query = new URLSearchParams();
                if (vehicle.dateTimeUtc) query.set('dateTimeUtc', vehicle.dateTimeUtc);
                query.set('view', 'report');
                navigate(`/vehicle/${normalizedPlate}?${query.toString()}`, {
                    state: { vehicle, initialView: 'report' },
                });
            } else {
                navigate(`/vehicle/${normalizedPlate}`);
            }
        } catch {
            navigate(`/vehicle/${normalizedPlate}`);
        }
    };

    const handleDelete = (row) => {
        modals.openConfirmModal({
            title: 'Remove drive-off record?',
            children: (
                <Text size="sm">
                    Remove the {formatCurrency(row.amount)} drive-off for {formatPlate(row.plate)}? This cannot be undone.
                </Text>
            ),
            labels: { confirm: 'Yes, remove', cancel: 'Cancel' },
            confirmProps: { color: 'red' },
            onConfirm: () => {
                setAllRows((prev) => prev.filter((r) => r.id !== row.id));
                toast.success('Record removed.');
            },
        });
    };

    const handlePay = (row) => {
        modals.openConfirmModal({
            title: 'Mark drive-off as paid?',
            children: (
                <Text size="sm">
                    Settle the {formatCurrency(row.amount)} drive-off for {formatPlate(row.plate)}? This cannot be undone.
                </Text>
            ),
            labels: { confirm: 'Yes, mark as paid', cancel: 'Cancel' },
            confirmProps: { color: 'green' },
            onConfirm: () => {
                setAllRows((prev) => prev.filter((r) => r.id !== row.id));
                toast.success('Drive-off marked as paid.');
            },
        });
    };

    const totalPages = Math.ceil(allRows.length / PAGE_SIZE);
    const rows = allRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const startIndex = allRows.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const endIndex = (page - 1) * PAGE_SIZE + rows.length;

    const thStyle = {
        fontSize: '12px',
        fontWeight: 600,
        color: '#64748B',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        padding: '12px 16px',
        borderBottom: '1px solid #E2E8F0',
        whiteSpace: 'nowrap',
        textAlign: 'left',
    };

    const tdStyle = {
        fontSize: '14px',
        color: '#334155',
        padding: '0 16px',
        height: '64px',
        borderBottom: '1px solid #F1F5F9',
        verticalAlign: 'middle',
        textAlign: 'left',
    };

    return (
        <div style={{ minHeight: '100vh', backgroundColor: '#F8FAFC' }}>
            <div style={{ paddingTop: '80px' }}>
                <div style={{ width: '100%', maxWidth: '1200px', margin: '0', padding: '24px 32px', textAlign: 'left' }}>

                    <div style={{ marginBottom: '28px' }}>
                        <h1 style={{ margin: 0, fontWeight: '700', fontSize: '30px', lineHeight: 1.2, color: '#111827', textAlign: 'left' }}>
                            Drive Off Report
                        </h1>
                        <p style={{ margin: 0, fontSize: '15px', color: '#6b7280', marginTop: '8px', lineHeight: 1.6, textAlign: 'left' }}>
                            These are all active drive-offs recorded at your site. If a vehicle re-enters your station — or any other station protected by DriveOffShield — an alarm will sound automatically.
                        </p>
                    </div>

                    <Paper shadow="xs" radius="md" p={0} style={{ overflow: 'hidden', backgroundColor: '#FFFFFF' }}>
                        {loading ? (
                            <Stack align="center" py={64} gap="sm">
                                <Loader size="sm" color="blue" />
                                <Text size="sm" c="gray.4">Loading drive-offs…</Text>
                            </Stack>
                        ) : error ? (
                            <Stack align="center" py={64} gap="sm">
                                <Text size="sm" c="red.5">{error}</Text>
                            </Stack>
                        ) : (
                            <>
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead style={{ backgroundColor: '#F8FAFC' }}>
                                            <tr>
                                                <th style={thStyle}>Rego</th>
                                                <th style={thStyle}>Images</th>
                                                <th style={thStyle}>Date &amp; Time</th>
                                                <th style={thStyle}>Amount</th>
                                                <th style={thStyle}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map((row, idx) => {
                                                const images = [row.detectionPlateImageUrl, row.vehicleImageUrl, row.regoPlateImageUrl]
                                                    .filter(url => url && url.trim());
                                                const isLast = idx === rows.length - 1;
                                                return (
                                                    <tr
                                                        key={row.id}
                                                        style={{ backgroundColor: '#FFFFFF' }}
                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F8FAFC'}
                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FFFFFF'}
                                                    >
                                                        <td style={{ ...tdStyle, borderBottom: isLast ? 'none' : tdStyle.borderBottom }}>
                                                            <button
                                                                type="button"
                                                                onClick={() => handlePlateClick(row.plate)}
                                                                style={{
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    padding: 0,
                                                                    cursor: 'pointer',
                                                                    fontFamily: 'monospace',
                                                                    fontSize: '14px',
                                                                    fontWeight: 600,
                                                                    letterSpacing: '0.04em',
                                                                    color: '#26A5DF',
                                                                    textDecoration: 'underline',
                                                                    textDecorationColor: 'rgba(38,165,223,0.4)',
                                                                    textUnderlineOffset: '3px',
                                                                }}
                                                            >
                                                                {formatPlate(row.plate)}
                                                            </button>
                                                        </td>

                                                        <td style={{ ...tdStyle, borderBottom: isLast ? 'none' : tdStyle.borderBottom }}>
                                                            <Group gap={6} wrap="nowrap">
                                                                {images.length > 0 ? (
                                                                    images.map((url, i) => (
                                                                        <img
                                                                            key={i}
                                                                            src={url}
                                                                            alt={`Evidence ${i + 1}`}
                                                                            style={{
                                                                                width: '44px', height: '44px',
                                                                                objectFit: 'cover', borderRadius: '8px',
                                                                                border: '1px solid #E2E8F0',
                                                                                display: 'block',
                                                                            }}
                                                                        />
                                                                    ))
                                                                ) : (
                                                                    <div style={{
                                                                        width: '44px', height: '44px', borderRadius: '8px',
                                                                        backgroundColor: '#F1F5F9', border: '1px solid #E2E8F0',
                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                    }}>
                                                                        <IconReceipt size={18} stroke={1.5} color="#94A3B8" />
                                                                    </div>
                                                                )}
                                                            </Group>
                                                        </td>

                                                        <td style={{ ...tdStyle, borderBottom: isLast ? 'none' : tdStyle.borderBottom }}>
                                                            <Text size="sm" c="gray.7">{formatDateTime(row.dateTimeUtc)}</Text>
                                                        </td>

                                                        <td style={{ ...tdStyle, borderBottom: isLast ? 'none' : tdStyle.borderBottom }}>
                                                            <Text size="sm" fw={600} c="gray.8">{formatCurrency(row.amount)}</Text>
                                                        </td>

                                                        <td style={{ ...tdStyle, borderBottom: isLast ? 'none' : tdStyle.borderBottom }}>
                                                            <Group gap="xs" wrap="nowrap">
                                                                <Tooltip label="Mark as paid" withArrow>
                                                                    <ActionIcon
                                                                        variant="filled"
                                                                        color="green"
                                                                        size="sm"
                                                                        radius="md"
                                                                        onClick={() => handlePay(row)}
                                                                    >
                                                                        <IconCurrencyDollar size={15} stroke={1.75} />
                                                                    </ActionIcon>
                                                                </Tooltip>
                                                                <Tooltip label="Delete" withArrow>
                                                                    <ActionIcon
                                                                        variant="subtle"
                                                                        color="red"
                                                                        size="sm"
                                                                        radius="md"
                                                                        onClick={() => handleDelete(row)}
                                                                    >
                                                                        <IconTrash size={15} stroke={1.75} />
                                                                    </ActionIcon>
                                                                </Tooltip>
                                                            </Group>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {allRows.length === 0 ? (
                                    <Stack align="center" py={64} gap="sm">
                                        <IconReceipt size={40} stroke={1} color="#CBD5E1" />
                                        <Text size="sm" c="gray.4">No active drive-offs</Text>
                                    </Stack>
                                ) : (
                                    <Group justify="space-between" align="center" px="xl" py="md"
                                        style={{ borderTop: '1px solid #F1F5F9' }}>
                                        <Text size="sm" c="gray.5">
                                            {startIndex}–{endIndex} of {allRows.length}
                                        </Text>
                                        <Group gap="xs">
                                            <button
                                                disabled={page === 1}
                                                onClick={() => setPage(1)}
                                                style={paginationBtn(page === 1)}
                                            >← First</button>
                                            <button
                                                disabled={page === 1}
                                                onClick={() => setPage((p) => p - 1)}
                                                style={paginationBtn(page === 1)}
                                            >← Prev</button>
                                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                                                .reduce((acc, p, idx, arr) => {
                                                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('ellipsis-' + p);
                                                    acc.push(p);
                                                    return acc;
                                                }, [])
                                                .map(p =>
                                                    typeof p === 'string' ? (
                                                        <span key={p} style={{ padding: '5px 4px', fontSize: '13px', color: '#CBD5E1' }}>…</span>
                                                    ) : (
                                                        <button
                                                            key={p}
                                                            onClick={() => setPage(p)}
                                                            style={paginationBtn(false, p === page)}
                                                        >{p}</button>
                                                    )
                                                )}
                                            <button
                                                disabled={page === totalPages}
                                                onClick={() => setPage((p) => p + 1)}
                                                style={paginationBtn(page === totalPages)}
                                            >Next →</button>
                                            <button
                                                disabled={page === totalPages}
                                                onClick={() => setPage(totalPages)}
                                                style={paginationBtn(page === totalPages)}
                                            >Last →</button>
                                        </Group>
                                    </Group>
                                )}
                            </>
                        )}
                    </Paper>

                </div>
            </div>
        </div>
    );
};

const paginationBtn = (disabled, active = false) => ({
    padding: '5px 12px',
    fontSize: '13px',
    fontWeight: active ? 600 : 400,
    borderRadius: '8px',
    border: active ? 'none' : '1px solid #E2E8F0',
    backgroundColor: active ? '#26A5DF' : disabled ? '#F8FAFC' : '#FFFFFF',
    color: active ? '#FFFFFF' : disabled ? '#CBD5E1' : '#334155',
    cursor: disabled ? 'default' : 'pointer',
    lineHeight: 1.5,
});

export default DriveOffReportPage;
